import type { Episode, SeriesSeason } from "@hybrid-iptv/types";

/**
 * Which episode comes after the one playing (owner, 2026-10-13: series need a Play next).
 *
 * An episode's progress key is `ep:<series id>:s<season>e<episode>` (vodHooks.episodeKey), so the
 * series and the place in it can be read back from what is playing, whichever screen started it
 * (the series page, Home's Continue watching, My List).
 */

export interface EpisodeRef {
  seriesId: string;
  season: number;
  episode: number;
}

const KEY = /^ep:(.+):s(\d+)e(\d+)$/;

/** The series and place an episode key names, or null when it is not one. */
export function parseEpisodeKey(key: string | null | undefined): EpisodeRef | null {
  const match = typeof key === "string" ? KEY.exec(key) : null;
  if (!match) return null;
  return { seriesId: match[1]!, season: Number(match[2]), episode: Number(match[3]) };
}

/**
 * The episode after S`season` E`episode`: the next number in the season, else the first of the next
 * season. Null at the end of the series or when the current one is not in the list. Specials and
 * gaps in numbering are walked in order, never skipped over to a guess.
 */
export function episodeAfter(seasons: readonly SeriesSeason[], season: number, episode: number): Episode | null {
  const ordered = [...seasons]
    .sort((a, b) => a.seasonNumber - b.seasonNumber)
    .flatMap((s) => [...s.episodes].sort((a, b) => a.episodeNumber - b.episodeNumber));
  const at = ordered.findIndex((e) => e.seasonNumber === season && e.episodeNumber === episode);
  if (at < 0) return null;
  return ordered[at + 1] ?? null;
}

/** How long before the end the card appears, and so how long the countdown runs. */
export const UP_NEXT_SECONDS = 15;

/** Remaining time at or below this counts as the end: play the next one. */
const END_SLOP_SECONDS = 0.6;

/** Shorter than this a "duration" is not a real episode length (still loading, a clip). */
const MIN_EPISODE_SECONDS = 60;

export type UpNextPhase = "hidden" | "countdown" | "go";

/**
 * What the Play next card does right now. Pure, for tests.
 *  - hidden: no next episode, the viewer put it away, or it is not near the end;
 *  - countdown: the last 15 seconds, the card is up and counting down;
 *  - go: the time is up (or the video ended): play the next episode.
 */
export function upNextPhase(input: {
  hasNext: boolean;
  dismissed: boolean;
  status: string;
  currentTime: number;
  duration: number;
}): UpNextPhase {
  if (!input.hasNext || input.dismissed) return "hidden";
  const known = Number.isFinite(input.duration) && input.duration >= MIN_EPISODE_SECONDS;
  if (input.status === "ended") return known ? "go" : "hidden";
  if (!known) return "hidden";
  const remaining = input.duration - input.currentTime;
  if (remaining <= END_SLOP_SECONDS) return input.currentTime > 0 ? "go" : "hidden";
  return remaining <= UP_NEXT_SECONDS ? "countdown" : "hidden";
}
